import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PROJECTS } from "@/constants/testIds";
import { toast } from "sonner";
import { ArrowLeft, Send, Trophy, Sparkles, CheckCircle2, Bot, User } from "lucide-react";

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const [proj, setProj] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [submission, setSubmission] = useState("");
  const [repoUrl, setRepoUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const scrollRef = useRef(null);

  const load = () => api.get(`/projects/${projectId}`).then((r) => setProj(r.data));
  useEffect(() => { load(); }, [projectId]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [proj?.chat?.length]);

  if (!proj) return <div className="label-mono animate-pulse">Loading…</div>;
  const spec = proj.spec || {};
  const isComplete = proj.status === "completed";

  const sendMsg = async () => {
    if (!input.trim()) return;
    setBusy(true);
    const text = input; setInput("");
    setProj((p) => ({ ...p, chat: [...(p.chat || []), { role: "user", content: text }] }));
    try {
      const { data } = await api.post("/projects/step", { project_id: projectId, message: text });
      setProj((p) => ({ ...p, chat: [...(p.chat || []), { role: "assistant", content: data.reply }] }));
    } catch { toast.error("AI unavailable"); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!submission.trim()) { toast.error("Add your submission first."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/projects/submit", { project_id: projectId, submission, repo_url: repoUrl || null });
      toast.success(`Reviewed! Score: ${data.review?.total ?? "—"}/100`);
      if (data.new_badges?.length) toast.success(`New badge: ${data.new_badges.join(", ")}`);
      if (data.xp?.awarded_xp) toast.success(`+${data.xp.awarded_xp} XP`);
      await load();
    } catch { toast.error("Submission failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid={PROJECTS.detailRoot} className="space-y-6 animate-fade-up">
      <Link to="/app/projects" className="inline-flex items-center gap-2 text-xs font-mono text-muted-foreground hover:text-primary">
        <ArrowLeft className="h-3.5 w-3.5" /> All projects
      </Link>

      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="label-mono text-primary">// {proj.path_title}</div>
            <h1 className="mt-1 font-display text-3xl font-black tracking-tighter">{spec.title}</h1>
            <p className="mt-2 text-sm text-muted-foreground max-w-3xl">{spec.description}</p>
          </div>
          {isComplete && (
            <div className="rounded-2xl border border-accent/40 bg-accent/10 px-5 py-3 text-center">
              <div className="label-mono text-accent">score</div>
              <div className="font-display text-3xl font-black">{proj.review?.total ?? "—"}</div>
            </div>
          )}
        </div>
        {spec.goal && (
          <div className="mt-4 rounded-xl border border-border bg-background p-3">
            <div className="label-mono">Goal</div>
            <div className="mt-1 text-sm">{spec.goal}</div>
          </div>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-5">
          <div className="label-mono mb-3">// mentor guide</div>
          <div ref={scrollRef} className="max-h-[50vh] space-y-4 overflow-y-auto pr-1">
            {(proj.chat || []).length === 0 && (
              <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
                Ask your mentor for guidance — describe your first step or ask a question.
              </div>
            )}
            {(proj.chat || []).map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "assistant" && <div className="grid h-7 w-7 place-items-center rounded-full border border-primary/40 bg-primary/10 text-primary"><Bot className="h-3.5 w-3.5" /></div>}
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "border border-border bg-muted" : "border border-primary/25 bg-primary/5"}`}>{m.content}</div>
                {m.role === "user" && <div className="grid h-7 w-7 place-items-center rounded-full border border-border bg-secondary"><User className="h-3.5 w-3.5" /></div>}
              </div>
            ))}
            {busy && <div className="text-sm text-muted-foreground"><span className="chat-thinking">Thinking</span></div>}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Input
              data-testid={PROJECTS.stepInput}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask for guidance…"
              className="h-10 rounded-full"
              onKeyDown={(e) => { if (e.key === "Enter") sendMsg(); }}
              disabled={busy || isComplete}
            />
            <Button data-testid={PROJECTS.stepSendBtn} onClick={sendMsg} disabled={busy || !input.trim() || isComplete} className="h-10 rounded-full btn-neon">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="label-mono mb-3">// steps</div>
            <ol className="space-y-2">
              {(spec.steps || []).map((s, i) => (
                <li key={i} className="flex gap-2 text-sm">
                  <span className="label-mono w-6">{String(i+1).padStart(2, "0")}</span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
          </div>
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="label-mono mb-3">// rubric</div>
            <ul className="space-y-1 text-sm">
              {(spec.rubric || []).map((r, i) => (
                <li key={i} className="flex items-center justify-between">
                  <span>{r.criterion}</span>
                  <span className="label-mono">{r.weight}%</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {!isComplete ? (
        <div className="rounded-2xl border border-primary/30 bg-primary/5 p-6">
          <div className="mb-3 flex items-center gap-2">
            <Trophy className="h-5 w-5 text-primary" />
            <div className="font-display text-xl font-bold">Ready to submit?</div>
          </div>
          <p className="text-sm text-muted-foreground">Paste your write-up / code / summary, and (optionally) a repo URL. Your mentor will score and give detailed feedback.</p>
          <Textarea
            data-testid={PROJECTS.submissionInput}
            value={submission}
            onChange={(e) => setSubmission(e.target.value)}
            placeholder="Your submission (write-up, code excerpts, links)…"
            className="mt-3 min-h-[140px] rounded-lg"
          />
          <Input
            value={repoUrl}
            onChange={(e) => setRepoUrl(e.target.value)}
            placeholder="Repo URL (optional)"
            className="mt-3 h-10 rounded-lg"
          />
          <Button
            data-testid={PROJECTS.submitBtn}
            onClick={submit}
            disabled={submitting || !submission.trim()}
            className="mt-4 rounded-full btn-neon font-mono text-xs uppercase tracking-widest"
          >
            <Sparkles className="mr-2 h-4 w-4" /> {submitting ? "Reviewing…" : "Submit for review"}
          </Button>
        </div>
      ) : (
        <div className="rounded-2xl border border-accent/40 bg-accent/5 p-6">
          <div className="mb-3 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-accent" />
            <div className="font-display text-xl font-bold">Reviewed · {proj.review?.verdict}</div>
          </div>
          {(proj.review?.scores || []).length > 0 && (
            <div className="mb-4">
              <div className="label-mono mb-2">// scores</div>
              <ul className="space-y-1 text-sm">
                {proj.review.scores.map((s, i) => (
                  <li key={i} className="flex items-center justify-between">
                    <span>{s.criterion}</span>
                    <span className="label-mono">{s.score}/100</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(proj.review?.strengths || []).length > 0 && (
            <div className="mb-3">
              <div className="label-mono mb-1">// strengths</div>
              <ul className="list-disc pl-5 text-sm">{proj.review.strengths.map((s,i)=>(<li key={i}>{s}</li>))}</ul>
            </div>
          )}
          {(proj.review?.improvements || []).length > 0 && (
            <div className="mb-3">
              <div className="label-mono mb-1">// improvements</div>
              <ul className="list-disc pl-5 text-sm">{proj.review.improvements.map((s,i)=>(<li key={i}>{s}</li>))}</ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
